import { imzalayanYeterliligi, yeterlilikCumlesi, type InspektorSecenegi, type InspektorSertifikasi, type NdtYontemi, type YeterlilikBulgusu } from "@oasis/domain";
import { cevir } from "@/lib/dil";
import { tarih } from "@/lib/veri";

/**
 * D-062: NDT raporu yükleme pencerelerinin (Finishing "Upload NDT report" / "+ NDT", Component "Upload Report PDF") saf
 * yardımcıları: imzalayan form alanları, uyarı adımına düşme kuralı, ön-seçim ve yüklü raporun yanındaki gösterim.
 * Kuralın kendisi alan paketindedir (`imzalayanSorulurMu`, `yuklemeImzalayanHatasi`); burada yalnız ekranın kararları durur.
 * D-064: seçilen imzalayanın sertifika yeterliliği de burada ölçülür (`secimYeterliligi`; kural alan paketinde:
 * `imzalayanYeterliligi`). Sonuç yalnız uyarıdır: sunucuya yeni alan gitmez, yükleme kabul edilir.
 */

/**
 * Registers › NDT inspectors'ın seçim listesi ([{id, ad, aktif}], pasifler dahil); oturum açmış her rol okur.
 * D-064: raporu yükleyebilen rolde her öğe ek olarak `sertifikalar` taşır (etkin NDT sertifikalarının özeti: yöntem, seviye,
 * geçerlilik sonu); öteki rolde ve eski sunucuda alan HİÇ yoktur, ekran o zaman yeterlilik ölçmez.
 */
export const NDT_INSPEKTOR_YOLU = "/dosya-belge/ndt-inspektorler";

/** Yüklü raporun imzalayanı (sunucudan). `id` sistemin verdiği kimliktir, ekrana yazılmaz; ekranda `ad` görünür. */
export interface Imzalayan { id: string; ad: string }

/**
 * Yükleme isteğine eklenecek form alanları (`dosyaYukle`): imzalayan seçiliyse `imzalayanlar` = inspektör kimliklerinin JSON
 * dizisi; seçili değilse ve kullanıcı uyarıyı onayladıysa `imzalayanOnay` = "true"; ikisi de yoksa alan yok (sunucu böyle bir
 * NDT yüklemesini reddeder). İkisi birlikte gönderilmez: imzalayan seçiliyken onayın anlamı yoktur.
 */
export function imzalayanAlanlari(secili: readonly string[], onay: boolean): Record<string, string> {
  if (secili.length) return { imzalayanlar: JSON.stringify([...secili]) };
  return onay ? { imzalayanOnay: "true" } : {};
}

/**
 * Uyarı adımının iki türü (tek parça çizer: `ImzalayanUyarisi`): "imzalayansiz" = imzalayan seçilmedi (D-062);
 * "yetersiz" = seçilen imzalayanın NDT sertifikası yetmiyor (D-064). İkisi aynı anda çıkmaz.
 */
export type UyariTuru = "imzalayansiz" | "yetersiz";

/**
 * Yükleme düğmesine basılınca ne olur. İmzalayan sorulmuyorsa istek gider. Sorulduğu hâlde seçili değilse İSTEK GİTMEZ, pencere
 * uyarı ve onay adımına geçer ("uyari"; bulgu verilmiş olsa da: imzalayansız yüklemede yeterlilik ölçülmez). D-064: imzalayan
 * seçili ve sertifikası yetersizse (`bulgular` dolu) istek yine gitmez, pencere sertifika uyarısına geçer ("yetersiz");
 * bulgu yoksa istek gider. `bulgular` verilmezse D-062'deki iki sonuç aynen döner.
 */
export function yuklemeAdimi(sorulur: boolean, secili: readonly string[], bulgular: readonly YeterlilikBulgusu[] = []): "gonder" | "uyari" | "yetersiz" {
  if (!sorulur) return "gonder";
  if (secili.length === 0) return "uyari";
  return bulgular.length ? "yetersiz" : "gonder";
}

/**
 * Uyarı adımı açıldıktan sonra iki düğmenin ("Confirm and upload without signer" ya da "Upload anyway", "Back") pasif kaldığı
 * süre (ms). Yükleme düğmesine çift dokunuşun ikincisi uyarının düğmelerine düşmesin: dar ekranda pencere alttan açılır ve yükleme
 * düğmesinin yerine "Back" gelir; kilit olmadan çift dokunuş uyarıyı okunmadan kapatır. Çift dokunuş aralığından uzun, bekletmeyecek
 * kadar kısa. Düğmelerin sırası bu yüzden değişmez: onay altta olsaydı çift dokunuş yanlışlıkla onaya dönüşürdü.
 */
export const UYARI_DUGME_KILIDI_MS = 400;

/** Seçili kimliklerin kütükteki kayıtları, seçim sırasıyla (çipler). Kütükte olmayan kimlik çizilmez ve gönderilmez. */
export function seciliInspektorler(secili: readonly string[], kutuk: readonly InspektorSecenegi[]): InspektorSecenegi[] {
  return [...new Set(secili)].flatMap((id) => {
    const k = kutuk.find((x) => x.id === id);
    return k ? [k] : [];
  });
}

/**
 * D-064: seçilen imzalayanların sertifika yeterliliği; boş liste = yeterli ya da ölçülmedi. Kural alan paketindedir
 * (`imzalayanYeterliligi`): `yontemler` raporun yöntemleri (Component'te adım adından, `adimYontemleri`; Finishing hat NDT
 * raporunda boş: yöntem aranmaz), `gun` raporun yüklendiği gün (`istanbulGunu()`). Kütükte olmayan kimlik sayılmaz (çizilmez ve
 * gönderilmez). Seçilenlerden birinde `sertifikalar` alanı YOKSA (rol sertifika özetini görmüyor, eski sunucu) hiç ölçülmez:
 * olmayan alan boş liste gibi okunsaydı her imzalayan için yanlış "no NDT certificate" çıkardı.
 */
export function secimYeterliligi(secili: readonly string[], kutuk: readonly InspektorSecenegi[], yontemler: readonly NdtYontemi[], gun: string): YeterlilikBulgusu[] {
  const imzalayanlar: { id: string; ad: string; sertifikalar: readonly InspektorSertifikasi[] }[] = [];
  for (const k of seciliInspektorler(secili, kutuk)) {
    if (!Array.isArray(k.sertifikalar)) return [];
    imzalayanlar.push({ id: k.id, ad: k.ad, sertifikalar: k.sertifikalar });
  }
  return imzalayanYeterliligi({ imzalayanlar, yontemler, gun });
}

/**
 * D-064: bulgunun ekrandaki neden satırı ("Selin Yaman: PT certificate expired on 14/03/2024"). Kalıp alan paketindedir
 * (`yeterlilikCumlesi`); geçerlilik sonu ({2}) ekranın tarih biçimiyle yazılır, ad ve yöntem kısaltmaları çevrilmez.
 */
export function bulguMetni(b: YeterlilikBulgusu): string {
  const { kalip, degerler } = yeterlilikCumlesi(b);
  return cevir(kalip, ...degerler.map((d, i) => (i === 2 ? tarih(d) : d)));
}

/** Adım kaydındaki PDF satırının imzalayan bilgisi: null (ya da alan yok) = bu adımda imzalayan sorulmaz, [] = imzalayan eksik. */
export interface PdfImzasi { ts: string; imzalayanlar?: readonly Imzalayan[] | null }

/**
 * Component penceresinin ön-seçimi: adımın imzalayanı olan SON PDF'inin (yükleme zamanına göre) aktif imzalayanları. Hardness
 * adımında aynı PDF kaynak başına yeniden yüklenir; ikinci kaynakta imzalayan yeniden seçilmesin diye. Pasif ya da kütükte
 * artık olmayan inspektör ön-seçilmez (yüklemede pasif inspektör seçilemez).
 */
export function onSecim(pdfler: readonly PdfImzasi[], kutuk: readonly InspektorSecenegi[]): string[] {
  const son = pdfler
    .filter((p) => (p.imzalayanlar?.length ?? 0) > 0)
    .sort((a, b) => (a.ts < b.ts ? -1 : a.ts > b.ts ? 1 : 0))
    .at(-1);
  const aktif = new Set(kutuk.filter((k) => k.aktif).map((k) => k.id));
  return [...new Set((son?.imzalayanlar ?? []).map((x) => x.id))].filter((id) => aktif.has(id));
}

/**
 * Yüklü NDT raporunun yanındaki gösterim (Finishing rapor çipi, Component "Document" listesi): imzalayan varsa adları
 * ("Signed by: …"), liste boşsa sarı "signer missing"; null ya da alan yoksa hiçbir şey (o yüklemede imzalayan sorulmaz).
 */
export type ImzalayanGosterimi = { tur: "yok" } | { tur: "eksik" } | { tur: "var"; adlar: string };

export function imzalayanGosterimi(imzalayanlar: readonly Imzalayan[] | null | undefined): ImzalayanGosterimi {
  if (!imzalayanlar) return { tur: "yok" };
  return imzalayanlar.length ? { tur: "var", adlar: imzalayanlar.map((x) => x.ad).join(", ") } : { tur: "eksik" };
}
